/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import axios from "axios";
import YAML from "yaml";
import {
  AnimationType,
  ChatMsgType,
  DialogueMessage,
  DialogueMessageType,
  DialogueValidation,
  Lesson,
  LessonType,
  User,
  UserRole,
} from "./types";

export function getCurrentUTC(): Date {
  return new Date(Date.now());
}

export function getFormattedDate(date?: Date): string {
  return (date || getCurrentUTC()).toISOString();
}

export function getPercent(mastery: number): string {
  return `${Math.round(mastery * 100)}%`;
}

export function copyAndSet<T>(array: T[], idx: number, value: T): T[] {
  if (idx >= array.length || idx < 0) {
    return [...array, value];
  }
  return [...array.slice(0, idx), value, ...array.slice(idx + 1)];
}

export function copyAndRemove<T>(array: T[], idx: number): T[] {
  if (idx >= array.length || idx < 0) {
    return array;
  }
  return [...array.slice(0, idx), ...array.slice(idx + 1)];
}

export function copyAndMove<T>(a: T[], moveFrom: number, moveTo: number): T[] {
  if (
    moveFrom < 0 ||
    moveFrom >= a.length ||
    moveTo < 0 ||
    moveTo >= a.length
  ) {
    return a;
  }
  const item = a[moveFrom];
  const removed = copyAndRemove(a, moveFrom);
  return [...removed.slice(0, moveTo), item, ...removed.slice(moveTo)];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function downloadFile(file: any, fileName: string): void {
  file.name = fileName;
  const link = document.createElement("a");
  const url = URL.createObjectURL(file);
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractErrorMessageFromError(err: any | unknown): string {
  if (axios.isAxiosError(err)) {
    return err.message || err.response?.data;
  } else if (err instanceof Error) {
    return err.message;
  } else {
    try {
      const error = JSON.stringify(err);
      return error;
    } catch (err) {
      return "Cannot stringify error, unknown error structure";
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function readYamlFile(file: File): Promise<any> {
  const data = await new Promise((resolve) => {
    const fileReader = new FileReader();
    fileReader.onload = () => resolve(fileReader.result);
    fileReader.readAsText(file);
  });
  if (data && typeof data === "string") {
    return YAML.parse(data);
  }
  throw new Error("Could not load YAML data from file");
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function downloadYamlFile(obj: any, fileName?: string): void {
  const file = new File(
    [YAML.stringify(obj)],
    `${fileName || nameToAlias(obj.name)}.yaml`,
    {
      type: "text/plain",
    }
  );
  const link = document.createElement("a");
  const url = URL.createObjectURL(file);
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
}

export function addQueryParam(
  url: string,
  paramName: string,
  paramValue: string
): string {
  const urlObject = new URL(url);
  urlObject.searchParams.append(paramName, paramValue);
  return urlObject.toString();
}

export function nameToAlias(str: string): string {
  return (str || "")
    .trim()
    .toLowerCase()
    .replaceAll("-", " ")
    .replaceAll(/\s\s+/g, " ")
    .replaceAll(" ", "-");
}

export function isLessonScored(lesson: Lesson): boolean {
  return (
    lesson.type === LessonType.MULTIPLE_CHOICE ||
    lesson.type === LessonType.OPENTUTOR ||
    lesson.type === LessonType.WEB_ACTIVE
  );
}

export function canUserEditUsers(user: User): boolean {
  return user.userRole === UserRole.ADMIN;
}

export function getLessonDuration(lesson: Lesson): number {
  let duration = 0;
  for (const r of lesson.resources) {
    duration += r.duration;
  }
  return duration;
}

export function getTimeString(ms: number): string {
  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (seconds >= 1 && minutes < 1) {
    return seconds == 1 ? seconds + " second" : seconds + " seconds";
  } else if (minutes >= 1 && hours < 1) {
    return minutes == 1 ? minutes + " minute" : minutes + " minutes";
  } else if (hours >= 1 && days < 1) {
    return hours == 1 ? hours + " hour" : hours + " hours";
  } else if (days >= 1) {
    return days == 1 ? days + " day" : days + " days";
  }
  return ms == 1 ? ms + " millisecond" : ms + " milliseconds";
}

export interface ValidationResult {
  type: ChatMsgType;
  isValid: boolean;
  response: DialogueMessage;
}
export function validateAnswer(
  text: string,
  validation: DialogueValidation | undefined
): ValidationResult {
  if (!validation) {
    return {
      isValid: true,
      type: ChatMsgType.FeedbackNeutral,
      response: {
        text: "",
        type: DialogueMessageType.None,
        animation: AnimationType.None,
      },
    };
  }
  const wordCount = text?.split(" ").length;
  const charCount = text.replace(/\s/g, "").length;
  const minWordCount = validation.minWordCount || 0;
  const minCharCount = validation.minCharacterCount || 0;
  const recWordCount = validation.wordCount || 0;
  const recCharCount = validation.characterCount || 0;
  if (text.length > 1000) {
    return {
      isValid: false,
      type: ChatMsgType.FeedbackNegative,
      response: {
        text: "Please keep your answer shorter than 1000 characters",
        type: DialogueMessageType.None,
        animation: AnimationType.None,
      },
    };
  }
  if (wordCount < minWordCount || charCount < minCharCount) {
    return {
      isValid: false,
      type: ChatMsgType.FeedbackNegative,
      response: {
        text: "Can you explain that in more detail?",
        type: DialogueMessageType.None,
        animation: AnimationType.None,
      },
    };
  }
  if (validation.requiredRegex && !text.match(validation.requiredRegex)) {
    return {
      isValid: false,
      type: ChatMsgType.FeedbackNegative,
      response: validation.feedback?.negative || {
        text: "The response you gave is invalid.",
        type: DialogueMessageType.None,
        animation: AnimationType.None,
      },
    };
  }
  if (
    wordCount >= recWordCount &&
    charCount >= recCharCount &&
    (!validation.regex || text.match(validation.regex))
  ) {
    return {
      isValid: true,
      type: ChatMsgType.FeedbackPositive,
      response: validation.feedback?.positive || {
        text: "Thanks, that is good to know.",
        type: DialogueMessageType.None,
        animation: AnimationType.None,
      },
    };
  }
  return {
    isValid: true,
    type: ChatMsgType.FeedbackNeutral,
    response: {
      text: "",
      type: DialogueMessageType.None,
      animation: AnimationType.None,
    },
  };
}
